import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  TextFieldProps,
  Chip,
  Box,
  SelectChangeEvent,
  Typography,
  Autocomplete,
} from "@mui/material";

interface CustomSelectProps {
  options: any[];
  multiple?: boolean;
}

export default function CustomSelect(
  props: TextFieldProps & CustomSelectProps
) {
  const {
    label,
    options,
    className = "",
    multiple = false,
    ...textFieldProps
  } = props;

  if (multiple) {
    // Extract only Select-compatible props
    const {
      value,
      onChange,
      disabled,
      error,
      helperText,
      margin,
      name
    } = textFieldProps;

    // Simple onChange handler that calls the original onChange with the value
    const handleSelectChange = (event: SelectChangeEvent<unknown>) => {
      if (onChange) {
        // Create a simple synthetic event with just the essential properties
        const syntheticEvent = {
          target: {
            value: event.target.value,
            name: name || "",
          },
        } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

        onChange(syntheticEvent);
      }
    };

    return (
      <FormControl
        fullWidth
        className={className}
        error={error}
        margin={margin}
      >
        <InputLabel id={`${label}-select-label`}>{label}</InputLabel>
        <Select
          value={value}
          onChange={handleSelectChange}
          disabled={disabled}
          labelId={`${label}-select-label`}
          multiple
          name={name}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {(selected as string[]).map((value) => {
                const option = options.find((opt) => opt.value === value);
                return (
                  <Chip
                    key={value}
                    label={option?.name || value}
                    size="small"
                    avatar={
                      option?.thumbnail ? (
                        <Box
                          component="img"
                          src={option.thumbnail}
                          alt={option.name}
                          referrerPolicy="no-referrer"
                          sx={{ width: 20, height: 20, objectFit: "cover" }}
                        />
                      ) : undefined
                    }
                  />
                );
              })}
            </Box>
          )}
        >
          {options?.map((option, index) => (
            <MenuItem key={index} value={option?.value}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {option?.thumbnail && (
                  <Box
                    component="img"
                    src={option.thumbnail}
                    alt={option.name}
                    referrerPolicy="no-referrer"
                    sx={{
                      width: 30,
                      height: 30,
                      objectFit: "cover",
                      borderRadius: 0.5,
                    }}
                  />
                )}
                <Typography variant="body2">{option?.name}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
        {helperText && (
          <Box
            sx={{
              color: error ? "error.main" : "text.secondary",
              fontSize: "0.75rem",
              mt: 0.5,
              ml: 1.75,
            }}
          >
            {helperText}
          </Box>
        )}
      </FormControl>
    );
  }

  // For single selection with search, use Autocomplete
  const {
    value,
    onChange,
    disabled,
    error,
    helperText,
    margin,
    name,
    ...singleTextFieldProps
  } = props;

  const selectedOption = options.find((opt) => opt.value === value) || null;

  const handleAutocompleteChange = (event: any, newValue: any) => {
    if (onChange) {
      const syntheticEvent = {
        target: {
          value: newValue?.value || "",
          name: name || "",
        },
      } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
      onChange(syntheticEvent);
    }
  };

  return (
    <Autocomplete
      options={options}
      value={selectedOption}
      onChange={handleAutocompleteChange}
      disabled={disabled}
      getOptionLabel={(option) => option?.name || ""}
      isOptionEqualToValue={(option, value) => option?.value === value?.value}
      renderInput={(params) => (
        <TextField
          {...params}
          {...singleTextFieldProps}
          label={props.label}
          error={error}
          helperText={helperText}
          margin={margin}
          name={name}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {option?.thumbnail && (
              <Box
                component="img"
                src={option.thumbnail}
                alt={option.name}
                referrerPolicy="no-referrer"
                sx={{
                  width: 30,
                  height: 30,
                  objectFit: "cover",
                  borderRadius: 0.5,
                }}
              />
            )}
            <Typography variant="body2">{option?.name}</Typography>
          </Box>
        </Box>
      )}
      ListboxProps={{
        style: {
          maxHeight: 300,
        },
      }}
    />
  );
}
